import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import "@blocknote/core/fonts/inter.css";
import "@blocknote/shadcn/style.css";
import "@/styles/blocknote-overrides.css";
import "@/styles/version-diff.css";
import { ConvexReactClient } from "convex/react";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { routeTree } from "./routeTree.gen";
import type { RouterContext } from "./routes/__root";
import { Toaster } from "react-hot-toast";
import { TooltipProvider } from "./components/shadcn/tooltip";
import { TOAST_STYLE } from "./components/ui/ActionToast";
import { AppErrorBoundary } from "./components/ui/AppErrorBoundary";
import BootLoader from "./components/loading/BootLoader";
import { initAnalytics } from "./lib/analytics";
import { installGlobalErrorHandlers } from "./lib/globalErrorHandlers";
import { installAppUpdateHandlers } from "./lib/appUpdate";
import { installBrowserZoomGuard } from "./lib/browserZoomGuard";
// Effet de bord à l'import : pose la classe `.dark` et écoute le système.
import "./lib/theme";
// Effet de bord à l'import : initialise i18next avant le premier render.
import "./i18n";

// Avant tout le reste : une erreur au montage doit déjà être capturée.
initAnalytics();
installGlobalErrorHandlers();
// Idem : un chunk manquant peut faire échouer le tout premier `import()` de la
// route, donc la récupération doit être armée avant le render.
installAppUpdateHandlers();
// Le zoom natif du navigateur n'a aucun sens dans une app canvas : on le
// neutralise avant le render pour qu'aucune surface ne puisse y échapper.
installBrowserZoomGuard();

const convex = new ConvexReactClient(import.meta.env.VITE_CONVEX_URL as string);

const router = createRouter({
  routeTree,
  context: {
    convex,
  } satisfies RouterContext,
});

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// État d'historique partagé : `from` mémorise la page d'origine pour la croix
// des settings (cf. hooks/useCloseSettings.ts).
declare module "@tanstack/history" {
  interface HistoryState {
    from?: string;
  }
}

const rootElement = document.getElementById("root")!;
if (!rootElement.innerHTML) {
  const root = createRoot(rootElement);
  root.render(
    <StrictMode>
      <AppErrorBoundary>
        <TooltipProvider delayDuration={500}>
          <Toaster toastOptions={{ style: TOAST_STYLE }} />
          <ConvexAuthProvider client={convex}>
            <RouterProvider router={router} />
            {/* Prend le relais du splash d'index.html, jusqu'à l'auth ou
                jusqu'au canvas (cf. le composant). */}
            <BootLoader />
          </ConvexAuthProvider>
        </TooltipProvider>
      </AppErrorBoundary>
    </StrictMode>,
  );
}
